import { useEffect, useMemo, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import useSession from '@aytm/app/swidget/base/session/useSession.ts'
import useTheme from '@aytm/app/swidget/base/modules/theme/useTheme.ts'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline.ts'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import ExitSimulation from '@aytm/app/swidget/base/questions/_common/ExitSimulation.tsx'
import { EcommerceBannerPlacement, IEcommerceQuestionViewProps } from '../types.ts'
import style from './Chewy.module.scss'
import Header from './components/Header.tsx'
import PromoStrip from './components/PromoStrip.tsx'
import Main from './components/Main.tsx'
import Footer from './components/Footer.tsx'
import AddToCartSidebar from './components/AddToCartSidebar.tsx'
import Product from './components/Product.tsx'
import Cart from './components/Cart.tsx'
import useScrollMemory from '../hooks/useScrollMemory.ts'

const ChewyView = ({ schema, response, checkout, setClosed, onUpdateResponse }: IEcommerceQuestionViewProps) => {
  const session = useSession()
  const theme = useTheme()
  const timeline = useTimeline()
  const viewport = useViewport()
  const na = useMemo(() => schema.answers.find(answer => answer.na), [schema])
  const totals = [
    response.answers.reduce((prev, cur) => prev + (cur.id === na?.id ? 0 : cur.v), 0),
    response.answers.reduce((prev, cur) => prev + cur.v * cur.price, 0)
  ]
  const findability = 'find' == schema.mode_type || (1 == schema.max_cart_size && !schema.shopping_cart)
  const pvRoot: HTMLElement = document.querySelector('.surveyWidgetFullsizeContainer')
  const { containerRef, registerCardRef, findCenterElement, restorePosition, scrollTop } = useScrollMemory()
  const [lastAddedIndex, setLastAddedIndex] = useState<number | null>(null)
  const [lastAddedFrom, setLastAddedFrom] = useState<'grid' | 'product'>('grid')
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [showCart, setShowCart] = useState<boolean>(false)
  const [mainPage, setMainPage] = useState<number>(1)
  const [needHint, setNeedHint] = useState<boolean>(true)

  // Restore Main grid scroll when returning from Cart / Product.
  useEffect(() => {
    if (selectedIndex === null && !showCart) restorePosition()
  }, [selectedIndex, showCart, restorePosition])

  // Selecting "None of these" finishes the question (mirrors CostcoView) — without this the
  // respondent records the NA answer but the survey never advances.
  useEffect(() => {
    if (!na) return
    const naResponse = response.answers.find(answer => answer.id === na.id)
    if (naResponse && naResponse.v > 0) checkout()
  }, [response, checkout, na])

  const productCap = !findability && schema.max_qty_per_product > 0 ? schema.max_qty_per_product : 0

  const onAddToCart = (index: number, qnt = 1) => {
    const answer = response.answers[index]
    const next = productCap ? Math.min(answer.v + qnt, productCap) : answer.v + qnt
    if (next === answer.v) return
    answer.v = next
    timeline.log({
      type: showCart ? 'edit_in_cart' : 'add_to_cart',
      ref: answer.id,
      target: showCart || selectedIndex !== null ? answer.v.toString() : '1'
    })
    if (!showCart) {
      setLastAddedIndex(index)
      setLastAddedFrom(selectedIndex !== null ? 'product' : 'grid')
    }
    onUpdateResponse(response)
  }

  // Absolute set: findability single-pick (qnt 1), cart quantity edit, and remove (qnt 0).
  const onSetQuantity = (index: number, qnt: number) => {
    const answer = response.answers[index]
    const next = productCap && qnt > 0 ? Math.min(qnt, productCap) : qnt
    if (answer.v === next) return
    answer.v = next
    if (next === 0) {
      timeline.log({ type: 'delete_from_cart', ref: answer.id })
    } else {
      timeline.log({
        type: showCart ? 'edit_in_cart' : 'add_to_cart',
        ref: answer.id,
        target: answer.v.toString()
      })
    }
    if (!showCart && next > 0) {
      setLastAddedIndex(index)
      setLastAddedFrom(selectedIndex !== null ? 'product' : 'grid')
    }
    onUpdateResponse(response)
  }

  const onItemSelect = (index: number, bannerPlacement?: EcommerceBannerPlacement) => {
    // Re-selecting the already-open product (e.g. tapping the active variant tile in Subproducts)
    // is a no-op — skip the redundant scroll reset and the unmatched open_popup log.
    if (index === selectedIndex) return
    if (bannerPlacement) {
      timeline.log({
        type: 'banner_click',
        ref: response.answers[index].id,
        context: { placement: bannerPlacement }
      })
    }
    scrollTop()
    // Switching straight from one product to another (variant picker on the product page):
    // close the currently-open product before opening the new one, so every open_popup has a
    // matching close_popup. From the grid/cart selectedIndex is null — there is nothing to close.
    if (selectedIndex !== null && selectedIndex !== index) {
      timeline.log({
        type: 'close_popup',
        ref: response.answers[selectedIndex].id
      })
    }
    timeline.log({
      type: 'open_popup',
      target: '0,0',
      ref: response.answers[index].id
    })
    if (showCart) {
      timeline.log({ type: 'close_cart' })
      setShowCart(false)
    }
    setSelectedIndex(index)
  }

  const onItemClose = () => {
    if (selectedIndex !== null) {
      timeline.log({
        type: 'close_popup',
        ref: response.answers[selectedIndex].id
      })
    }
    setSelectedIndex(null)
  }

  const onSidebarClose = () => {
    setLastAddedIndex(null)
  }

  const onCartOpen = () => {
    if (selectedIndex !== null) onItemClose()
    timeline.log({ type: 'open_cart' })
    setShowCart(true)
  }

  const onCartClose = () => {
    timeline.log({ type: 'close_cart' })
    setShowCart(false)
  }

  const onHomeClick = () => {
    if (selectedIndex !== null) return onItemClose()
    if (showCart) return onCartClose()
    scrollTop()
  }

  if (theme.style?.pv && !theme.style?.flutter && !pvRoot) return null

  return (
    <Dialog
      maskClassName={`${style.dialog} ${session.themeClassNames} ${viewport.mobile ? style.mobile : ''}`}
      appendTo={theme.style?.pv && pvRoot ? pvRoot : session.document?.body}
      onHide={() => {}}
      maximizable={true}
      maximized={true}
      visible={true}
    >
      <div className={style.container} ref={containerRef} onScroll={findCenterElement}>
        <Header
          schema={schema}
          total={totals[0]}
          findability={findability}
          onCartOpen={onCartOpen}
          onHomeClick={onHomeClick}
        />
        <PromoStrip priceMask={schema.price_mask} />
        {selectedIndex !== null ? (
          <Product
            schema={schema}
            answer={schema.answers[selectedIndex]}
            findability={findability}
            amount={response.answers[selectedIndex].v}
            onAddToCart={onAddToCart}
            onSetQuantity={onSetQuantity}
            onItemSelect={onItemSelect}
            onItemClose={onItemClose}
          />
        ) : showCart ? (
          <Cart
            schema={schema}
            response={response}
            totals={totals}
            findability={findability}
            onUpdateResponse={onUpdateResponse}
            onItemSelect={onItemSelect}
            onAddToCart={onAddToCart}
            onSetQuantity={onSetQuantity}
            onCartClose={onCartClose}
            checkout={checkout}
            scrollTop={scrollTop}
          />
        ) : (
          <Main
            schema={schema}
            findability={findability}
            page={mainPage}
            response={response}
            onPageChange={setMainPage}
            onItemSelect={onItemSelect}
            onAddToCart={onAddToCart}
            onSetQuantity={onSetQuantity}
            registerCardRef={registerCardRef}
            scrollTop={scrollTop}
          />
        )}
        <Footer
          scrollTop={scrollTop}
          onHomeClick={onHomeClick}
          showHint={() => setNeedHint(true)}
          closeSimulation={setClosed}
        />
      </div>
      {/* Findability picks auto-submit; the confirmation sidebar would only flash before advancing. */}
      {!findability && lastAddedIndex !== null && (
        <AddToCartSidebar
          schema={schema}
          response={response}
          addedIndex={lastAddedIndex}
          totals={totals}
          variant={lastAddedFrom}
          onClose={onSidebarClose}
          onCartOpen={onCartOpen}
          checkout={checkout}
        />
      )}
      <ExitSimulation
        setClosed={setClosed}
        needExternalHint={needHint}
        setExternalHint={viewport.mobile ? setNeedHint : null}
        hintText={schema.instruction}
      />
    </Dialog>
  )
}

export default ChewyView
